/**
 * Investor Service for Portal
 *
 * Handles fetching investor entity details for the authenticated contact
 */

import { investorPortalApiClient } from '../../shared/utils/api-client';

/**
 * Investor entity interface matching the backend PortalInvestor schema
 * and frontend InvestorEntity interface
 */

export interface InvestorEntityBankDetails {
  accountHolder: string;
  bankName: string;
  iban: string;
  swift?: string;
  accountNumber?: string;
  routingNumber?: string;
  bankCountry?: string;
  bankAddress?: string;
}

interface InvestorComplianceInfo {
  kycStatus: 'pending' | 'approved' | 'rejected';
  amlStatus: 'pending' | 'approved' | 'rejected';
  lastReviewDate?: string;
  nextReviewDate?: string;
}

export interface InvestorEntity {
  id: string;
  name: string;
  type: 'individual' | 'company' | 'fund' | 'trust';
  taxId?: string;
  registrationNumber?: string;
  incorporationDate?: string;
  jurisdiction?: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    postcode: string;
    country: string;
  };
  contacts: {
    primary: {
      name: string;
      email: string;
      phone: string;
      role: string;
      title: string;
    };
    secondary: Array<{
      name: string;
      email: string;
      phone: string;
      role: string;
      title: string;
    }>;
  };
  bankDetails?: InvestorEntityBankDetails;
  complianceInfo?: InvestorComplianceInfo;
}

export interface IbanValidationResult {
  is_valid: boolean;
  formatted_iban: string;
  swift_code: string;
  bank_name: string;
  country_code: string;
}

export interface SwiftValidationResult {
  is_valid: boolean;
  swift_code: string;
  bank_name: string;
  country_code: string;
}

export class InvestorService {
  /**
   * Get all investor entities accessible by the current contact
   */
  static async getInvestors(): Promise<InvestorEntity[]> {
    return investorPortalApiClient.get<InvestorEntity[]>('/portal/investors');
  }

  /**
   * Validate an IBAN and resolve the matching SWIFT/BIC and bank name.
   *
   * Routed through the investor portal API client so it carries the portal
   * auth token, and hits the portal-namespaced endpoint that is guarded by
   * the portal CHANGE_DATA module rather than the staff banking route.
   */
  static async validateIban(iban: string): Promise<IbanValidationResult> {
    return investorPortalApiClient.post<IbanValidationResult>(
      '/portal/investors/validate-iban',
      { iban: iban.trim() }
    );
  }

  /**
   * Validate a SWIFT/BIC and resolve its country and bank name.
   *
   * Used for non-IBAN (e.g. US) accounts, where the BIC is the bank anchor.
   * Routed through the investor portal API client so it carries the portal
   * auth token, and hits the portal-namespaced endpoint that is guarded by
   * the portal CHANGE_DATA module rather than the staff banking route.
   */
  static async validateSwift(swift: string): Promise<SwiftValidationResult> {
    return investorPortalApiClient.post<SwiftValidationResult>(
      '/portal/investors/validate-swift',
      { swift: swift.trim() }
    );
  }
}
